import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaDiscord,
  FaHome,
  FaBars,
  FaUser,
  FaHeart,
  FaSignInAlt,
  FaUserPlus,
  FaSignOutAlt,
  FaSearch,
  FaFilter,
  FaTimes,
  FaBalanceScale,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext.jsx";
import AnimeSearchBar from "./SearchBar.jsx";
import api from "../../api.js";
import { useSocket } from "../../context/SocketContext.jsx";
import useAnimeStore from "../../Store/AnimeStore.js";
import NotificationsBell from "./NotificationsBell.jsx";

const Navbar = () => {
  // State and context
  const [isOpen, setIsOpen] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showGenreFilter, setShowGenreFilter] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({});
  const [allGenres, setAllGenres] = useState([]);
  const animeList = useAnimeStore((state) => state.allAnimes);
  const { socket, joinProfileRoom, leaveProfileRoom } = useSocket();

  useEffect(() => {
    if (animeList) {
      const fetchGenresFromMongo = async () => {
        try {
          const genreSet = new Set();
          animeList.forEach((anime) => {
            (anime.genres || []).forEach((genre) => genreSet.add(genre));
          });
          setAllGenres(Array.from(genreSet).sort());
        } catch (err) {
          console.error("Failed to fetch genres:", err);
        }
      };
      fetchGenresFromMongo();
    }
  }, [animeList]);

  useEffect(() => {
    if (!user?.uid) return;
    let mounted = true;
    const fetchProfile = async () => {
      try {
        const res = await api.get(`/users/profile/${user.uid}`);
        const data = res.data || {};
        const normalized = data.profile
          ? {
            uid: data.uid || data.profile.uid,
            username: data.profile.username ?? data.username,
            photoURL: data.profile.photoURL ?? data.photoURL,
            bannerURL: data.profile.bannerURL ?? data.bannerURL,
            gradientColorTop:
              data.profile.gradientColorTop ?? data.gradientColorTop,
            gradientColorBottom:
              data.profile.gradientColorBottom ?? data.gradientColorBottom,
          }
          : {
            uid: data.uid ?? data._id,
            username: data.username,
            photoURL: data.photoURL,
            bannerURL: data.bannerURL,
            gradientColorTop: data.gradientColorTop,
            gradientColorBottom: data.gradientColorBottom,
          };
        if (mounted) setProfile(normalized);
      } catch (err) {
        console.error("Failed to fetch profile:", err);
      }
    };
    fetchProfile();

    try {
      joinProfileRoom(user.uid);
      const onProfileUpdated = (data) => {
        if (data.uid === user.uid) setProfile((p) => ({ ...p, ...data }));
      };
      socket?.on?.("profileUpdated", onProfileUpdated);
      return () => {
        mounted = false;
        leaveProfileRoom(user.uid);
        socket?.off?.("profileUpdated", onProfileUpdated);
      };
    } catch (e) {
      return () => {
        mounted = false;
      };
    }
  }, [user, socket]);

  useEffect(() => {
    if (isOpen) {
      setShowMobileSearch(false);
      setShowGenreFilter(false);
    }
  }, [isOpen]);

  // Close overlays on Escape key and lock body scroll while any overlay is open
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") closeAllOverlays();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);



  const displayName = profile.username || user?.displayName || "User";
  const profileImage =
    profile.photoURL ||
    "https://cdn.vectorstock.com/i/2000v/97/68/account-avatar-dark-mode-glyph-ui-icon-vector-44429768.avif";

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
      setIsOpen(false);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const toggleNavbar = () => {
    setIsOpen(!isOpen);
  };

  const closeAllOverlays = () => {
    setIsOpen(false);
    setShowMobileSearch(false);
    setShowGenreFilter(false);
  };

  return (
    <>
      {/* New layout wrapper: fixed top bar with rounded bottom (user's structure) */}
      <div className="flex justify-center">
        <div className="hidden lg:block mb-[4rem] rounded-b-[25px] h-[4rem] w-[90%] max-w-screen-2xl backdrop-blur-lg bg-darkGrayl bg-opacity-80 border-b border-gray-700 shadow-xl fixed top-0 left-1/2 -translate-x-1/2 z-40">
          <div className="flex justify-between items-center h-[4rem] px-4">
            {/* Left: Burger + Logo + Nav links (Home, Wishlist) */}
            <div className="flex items-center gap-3">
              <button
                onClick={toggleNavbar}
                aria-label="Toggle menu"
                aria-expanded={isOpen}
                className="text-white bg-darkGrayl bg-opacity-70 p-2 rounded-full hover:bg-opacity-90 transition-all"
              >
                <FaBars size={20} />
              </button>

              <Link
                to="/"
                onClick={closeAllOverlays}
                className="flex items-center"
              >
                <h1 className="text-white text-xl font-bold">H!N-ANIME</h1>
              </Link>

              {/* Desktop nav links */}
              <nav className="hidden lg:flex items-center gap-3 ml-3">
                <Link
                  to="/"
                  onClick={closeAllOverlays}
                  className="text-white hover:text-blue-300 px-3 py-1 rounded-md"
                >
                  Home
                </Link>
                {user && (
                  <Link
                    to="/wishlist"
                    onClick={closeAllOverlays}
                    className="text-white hover:text-blue-300 px-3 py-1 rounded-md"
                  >
                    Wishlist
                  </Link>
                )}
              </nav>
            </div>

            {/* Middle: Search (flexible) */}
            <div className="flex-1 mx-4">
              <AnimeSearchBar />
            </div>

            {/* Right: Discord, DMCA, Genres, Bell, Auth/Profile */}
            <div className="flex items-center gap-3">
              {/* Discord (external) - desktop only */}
              <a
                href="https://discord.gg/2JBnqk2kne"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden lg:inline-flex items-center text-white bg-gray-800 bg-opacity-70 p-2 rounded-full hover:bg-opacity-90 transition-all"
                onClick={closeAllOverlays}
                aria-label="Open Discord"
              >
                <FaDiscord size={18} />
              </a>

              {/* DMCA page link - desktop only */}
              <Link
                to="/dmca"
                onClick={closeAllOverlays}
                className="hidden lg:inline-flex items-center text-white bg-gray-800 bg-opacity-70 p-2 rounded-full hover:bg-opacity-90 transition-all"
                aria-label="DMCA"
              >
                <FaBalanceScale size={18} />
              </Link>

              <div className="relative">
                <button
                  onClick={() => setShowGenreFilter(!showGenreFilter)}
                  className="flex items-center gap-2 text-white bg-gray-800 hover:bg-gray-700 px-3 py-2 rounded-lg transition-colors"
                >
                  <FaFilter />
                  <span className="hidden lg:inline">Genres</span>
                </button>

                {showGenreFilter && (
                  <div className="absolute right-0 mt-2 w-48 bg-darkGray rounded-lg shadow-lg z-50 p-2 genre-filter-dropdown">
                    <div className="flex justify-between items-center mb-2 px-2">
                      <span className="text-white font-medium">
                        Filter by Genre
                      </span>
                      <button
                        onClick={() => setShowGenreFilter(false)}
                        className="text-gray-400 hover:text-white"
                      >
                        <FaTimes />
                      </button>
                    </div>
                    <div className="space-y-1">
                      {allGenres.map((genre) => (
                        <Link
                          key={genre}
                          to={`/genre/${genre.toLowerCase()}`}
                          onClick={() => {
                            setShowGenreFilter(false);
                            closeAllOverlays();
                          }}
                          className="block px-3 py-2 text-white hover:bg-gray-700 rounded transition-colors genre-item"
                        >
                          {genre}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {user && <NotificationsBell />}

              {/* Auth / Profile area */}
              {user ? (
                <div className="flex items-center gap-3">
                  <Link to="/profile">
                    <img
                      src={profileImage}
                      alt="User"
                      className="h-8 w-8 rounded-full object-cover border border-white"
                    />
                  </Link>
                  <span className="text-blue-300 hidden lg:inline">
                    Hi, {displayName}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Link
                    to="/login"
                    onClick={closeAllOverlays}
                    className="text-white px-3 py-1 rounded-lg hover:bg-gray-700"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    onClick={closeAllOverlays}
                    className="text-white bg-blue-600 px-3 py-1 rounded-lg hover:bg-blue-700"
                  >
                    Sign Up
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile top bar (kept for small screens) */}
      <div className="fixed top-0 left-0 right-0 z-30 lg:hidden backdrop-blur-lg bg-darkGrayl bg-opacity-80 border-b border-gray-700 shadow-xl flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2">
          <button
            onClick={toggleNavbar}
            className="text-white bg-darkGrayl bg-opacity-70 p-2 rounded-full hover:bg-opacity-90 transition-all"
            aria-label="Toggle menu"
          >
            <FaBars size={24} />
          </button>
        </div>

        <Link to="/">
          <h1 className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-xl font-bold select-none">
            H!N-ANIME
          </h1>
        </Link>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setShowGenreFilter(true);
              setShowMobileSearch(false);
            }}
            className="text-white bg-darkGrayl bg-opacity-70 p-2 rounded-full hover:bg-opacity-90 transition-all"
            aria-label="Open genre filter"
          >
            <FaFilter size={20} />
          </button>
          {user && <NotificationsBell />}
          <button
            onClick={() => {
              setShowMobileSearch(true);
              setShowGenreFilter(false);
            }}
            className="text-white bg-darkGrayl bg-opacity-70 p-2 rounded-full hover:bg-opacity-90 transition-all"
            aria-label="Open search"
          >
            <FaSearch size={22} />
          </button>
        </div>
      </div>

      {/* Mobile search overlay */}
      {showMobileSearch && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-80 lg:hidden"
          onClick={closeAllOverlays}
          aria-hidden="true"
        >
          <div
            className="w-full max-w-md mt-16 px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-2">
              <span className="text-white text-lg font-semibold">
                Search Anime
              </span>
              <button
                onClick={() => setShowMobileSearch(false)}
                className="text-white text-2xl px-2"
                aria-label="Close search"
              >
                <FaTimes />
              </button>
            </div>
            <AnimeSearchBar onResultClick={() => setShowMobileSearch(false)} />
          </div>
        </div>
      )}

      {/* Mobile genre filter overlay (kept as before) */}
      {showGenreFilter && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-80 lg:hidden"
          onClick={closeAllOverlays}
          aria-hidden="true"
        >
          <div
            className="w-full max-w-md mt-16 px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <span className="text-white text-lg font-semibold">
                Filter by Genre
              </span>
              <button
                onClick={() => setShowGenreFilter(false)}
                className="text-white text-2xl px-2"
                aria-label="Close genre filter"
              >
                <FaTimes />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {allGenres.map((genre) => (
                <Link
                  key={genre}
                  to={`/genre/${genre.toLowerCase()}`}
                  onClick={() => {
                    setShowGenreFilter(false);
                    closeAllOverlays();
                  }}
                  className="block px-4 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-center"
                >
                  {genre}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sidebar (same items) */}
      <header
        className={`fixed z-40 top-0 left-0 h-full transform transition-all duration-300 ease-in-out`}
        style={{ left: isOpen ? 0 : "-100%" }}
        aria-hidden={!isOpen}
      >
        <div className="mt-4 ml-4 lg:ml-6">
          <Link to="/" onClick={closeAllOverlays}>
            <img
              src="https://i.postimg.cc/XvdWdLrm/hinanime-logo-removebg-preview.png"
              className="h-16 w-auto relative z-20 hover:opacity-90 transition-opacity"
              alt="Logo"
            />
          </Link>
        </div>

        <nav className="backdrop-blur-lg bg-darkGrayl bg-opacity-80 border-r border-gray-700 shadow-xl h-full w-48 fixed top-0 left-0 flex flex-col items-start pt-24 pb-8 text-white overflow-y-auto">
          <ul className="space-y-2 w-full">
            <li>
              <Link
                to="/"
                onClick={closeAllOverlays}
                className="flex items-center space-x-3 py-3 px-4 rounded-lg hover:bg-gray-800 hover:text-blue-400 transition-colors duration-200"
              >
                <FaHome className="text-xl" />
                <span className="font-medium">HOME</span>
              </Link>
            </li>

            {user && (
              <li>
                <Link
                  to="/wishlist"
                  onClick={closeAllOverlays}
                  className="flex items-center space-x-3 py-3 px-4 rounded-lg hover:bg-gray-800 hover:text-blue-400 transition-colors duration-200"
                >
                  <FaHeart className="text-xl" />
                  <span className="font-medium">WISHLIST</span>
                </Link>
              </li>
            )}

            <li>
              <a
                href="https://discord.gg/2JBnqk2kne"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeAllOverlays}
                className="flex items-center space-x-3 py-3 px-4 rounded-lg hover:bg-gray-800 hover:text-blue-400 transition-colors duration-200"
              >
                <FaDiscord className="text-xl" />
                <span className="font-medium">DISCORD</span>
              </a>
            </li>

            <li>
              <Link
                to="/dmca"
                onClick={closeAllOverlays}
                className="flex items-center space-x-3 py-3 px-4 rounded-lg hover:bg-gray-800 hover:text-blue-400 transition-colors duration-200"
              >
                <FaBalanceScale className="text-xl" />
                <span className="font-medium">DMCA</span>
              </Link>
            </li>

            {/* Authenticated User */}
            {user ? (
              <>
                <li className="border-t border-gray-700 mt-4 pt-4">
                  <div className="flex items-center space-x-3 py-2 px-4">
                    <img
                      src={profileImage}
                      alt="User"
                      className="h-8 w-8 rounded-full object-cover border border-white"
                    />
                    <span className="font-medium text-blue-400">
                      Hi, {displayName}
                    </span>
                  </div>
                </li>
                <li>
                  <Link
                    to="/profile"
                    onClick={closeAllOverlays}
                    className="flex items-center space-x-3 py-3 px-4 rounded-lg hover:bg-gray-800 hover:text-blue-400 transition-colors duration-200"
                  >
                    <FaUser className="text-xl" />
                    <span className="font-medium">PROFILE</span>
                  </Link>
                </li>
                <li>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-3 w-full py-3 px-4 rounded-lg bg-gray-700 hover:bg-gray-800 hover:text-red-400 transition-colors duration-200"
                  >
                    <FaSignOutAlt className="text-xl" />
                    <span className="font-medium">LOGOUT</span>
                  </button>
                </li>
              </>
            ) : (
              <>
                <li className="border-t border-gray-700 mt-4 pt-4">
                  <Link
                    to="/login"
                    onClick={closeAllOverlays}
                    className="flex items-center space-x-3 py-3 px-4 rounded-lg hover:bg-gray-800 hover:text-blue-400 transition-colors duration-200"
                  >
                    <FaSignInAlt className="text-xl" />
                    <span className="font-medium">LOGIN</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/signup"
                    onClick={closeAllOverlays}
                    className="flex items-center space-x-3 py-3 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors duration-200"
                  >
                    <FaUserPlus className="text-xl" />
                    <span className="font-medium">SIGN UP</span>
                  </Link>
                </li>
                <li className="mt-4"></li>
              </>
            )}
          </ul>
        </nav>
      </header>

      {/* Overlay for sidebar (clicking anywhere outside should close it) */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={closeAllOverlays}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default Navbar;
